/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { HtmlHeadInclude } from '@msdyn365-commerce/core';
import * as React from 'react';
import { formatSchemaText } from './utils/format-schema-text';

export interface ProductListPageSummaryProps {
    title?: string;
    description?: string;
    canonicalUrl?: string;
    sharingImageUrl?: string;
}

const renderOgTags = (title: string | undefined, description: string | undefined, sharingImageUrl: string | undefined): JSX.Element => (
    <HtmlHeadInclude>
        {title && <meta property='og:title' content={title} />}
        {description && <meta property='og:description' content={description} />}
        {sharingImageUrl && <meta property='og:image' content={sharingImageUrl} />}
        <meta property='og:type' content='website' />
    </HtmlHeadInclude>
);

const renderTwitterTags = (
    title: string | undefined,
    description: string | undefined,
    sharingImageUrl: string | undefined
): JSX.Element => (
    <HtmlHeadInclude>
        {title && <meta name='twitter:title' content={title} />}
        {description && <meta name='twitter:description' content={description} />}
        {sharingImageUrl && <meta name='twitter:image' content={sharingImageUrl} />}
        <meta name='twitter:card' content='summary_large_image' />
    </HtmlHeadInclude>
);

const ProductListPageSummary: React.FC<ProductListPageSummaryProps> = props => {
    const { title, description, canonicalUrl, sharingImageUrl } = props;

    const pageTitle = title;
    const formattedDescription = formatSchemaText(description);

    return (
        <>
            <HtmlHeadInclude>
                {pageTitle && <title>{pageTitle}</title>}
                {formattedDescription && <meta name='description' content={formattedDescription} />}
                {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}
            </HtmlHeadInclude>
            {renderOgTags(pageTitle, formattedDescription, sharingImageUrl)}
            {renderTwitterTags(pageTitle, formattedDescription, sharingImageUrl)}
        </>
    );
};

export default ProductListPageSummary;
